import React from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  MONO_FONT,
  PAGE_FONT,
  SectionHead,
  headCellSx,
  numCellSx,
  textCellSx,
} from "../../components/metrics/metricsUi";
import { formatCount, formatCurrency } from "../../components/metrics/metricsFormat";
import { formatDate } from "../../utils/formatDate";
import { isMobile } from "../../globalStyles/mobile";

const SORTABLE = [
  { id: "age_days", label: "Age", align: "right" },
  { id: "stage", label: "Stage", align: "left" },
  { id: "return_units", label: "Units", align: "right" },
  { id: "return_value", label: "Value", align: "right" },
  { id: "requested_at", label: "Requested", align: "left" },
];

const Stat = ({ label, value, ui }) => (
  <Box>
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 600,
        color: ui.colors.textTertiary,
        letterSpacing: "0.03em",
        textTransform: "uppercase",
        mb: 0.4,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 16,
        fontWeight: 650,
        fontVariantNumeric: "tabular-nums",
        color: ui.colors.textPrimary,
        fontFamily: MONO_FONT,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const OpenReturnAgeDrawer = ({
  open,
  onClose,
  ui,
  ageBucket,
  payload,
  loading,
  error,
  stage,
  onStageChange,
  sort,
  onSort,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
}) => {
  const { colors } = ui;
  const summary = payload?.summary;
  const stages = payload?.stages || [];
  const rows = payload?.data || [];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile() ? "100%" : 880,
          maxWidth: "100%",
          backgroundColor: ui.darkMode ? colors.bgPrimary : "#F5F6F8",
          fontFamily: PAGE_FONT,
        },
      }}
    >
      <Box sx={{ px: 2.5, pt: 2, pb: 1.25, display: "flex", alignItems: "flex-start" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SectionHead
            title={`${ageBucket || ""} days`}
            subtitle="In-progress returns requested in this period. Calendar days since requested date."
          />
        </Box>
        <IconButton onClick={onClose} aria-label="Close age bucket" size="small" sx={{ mt: 0.25 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 2.5, pb: 3, overflowY: "auto" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
            {error}
          </Alert>
        )}

        <Box sx={{ ...ui.card, px: 2, py: 1.75, mb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile() ? "repeat(2, 1fr)" : "repeat(5, 1fr)",
              gap: 1.5,
            }}
          >
            <Stat ui={ui} label="Open returns" value={formatCount(summary?.count)} />
            <Stat ui={ui} label="Units" value={formatCount(summary?.units)} />
            <Stat ui={ui} label="Return value" value={formatCurrency(summary?.value)} />
            <Stat
              ui={ui}
              label="Average age"
              value={summary?.avg_age_days == null ? "—" : `${summary.avg_age_days} days`}
            />
            <Stat
              ui={ui}
              label="Oldest"
              value={summary?.oldest_age_days == null ? "—" : `${summary.oldest_age_days} days`}
            />
          </Box>
        </Box>

        {stages.length > 0 && (
          <Box sx={{ ...ui.card, px: 2, pt: 1.5, pb: 1.25, mb: 2 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: colors.textTertiary,
                letterSpacing: "0.03em",
                textTransform: "uppercase",
                mb: 1,
              }}
            >
              Current stage
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {stages.map((row) => {
                const selected = stage === row.stage;
                return (
                  <Box
                    key={row.stage || "blank"}
                    component="button"
                    type="button"
                    onClick={() => onStageChange(selected ? null : row.stage)}
                    sx={{
                      fontFamily: PAGE_FONT,
                      fontSize: 12.5,
                      fontWeight: selected ? 650 : 500,
                      px: 1.25,
                      py: 0.6,
                      borderRadius: "8px",
                      border: `1px solid ${selected ? ui.accent : ui.borderStrong}`,
                      backgroundColor: selected ? ui.accentSoft : colors.bgPaper,
                      color: colors.textPrimary,
                      cursor: "pointer",
                      textTransform: "none",
                    }}
                  >
                    {row.label || row.stage || "—"}
                    <Box
                      component="span"
                      sx={{
                        ml: 0.75,
                        fontVariantNumeric: "tabular-nums",
                        color: colors.textSecondary,
                      }}
                    >
                      {formatCount(row.count)}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        <Box sx={{ ...ui.card, px: 1.25, pt: 1, pb: 0.5 }}>
          {loading && !payload ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : rows.length === 0 && !loading ? (
            <Typography sx={{ fontSize: 13, color: colors.textTertiary, px: 1, py: 3 }}>
              No open returns in this age bucket
              {stage ? ` at stage ${stage}` : ""}.
            </Typography>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx(ui)}>Return</TableCell>
                    <TableCell sx={headCellSx(ui)}>Order</TableCell>
                    {SORTABLE.map((col) => (
                      <TableCell
                        key={col.id}
                        sx={headCellSx(ui, {
                          textAlign: col.align,
                          cursor: "pointer",
                        })}
                        onClick={() => onSort(col.id)}
                      >
                        <TableSortLabel
                          active={sort.field === col.id}
                          direction={sort.field === col.id ? sort.dir : "desc"}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell sx={headCellSx(ui)}>Channel</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row._id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => onRowClick(row)}
                    >
                      <TableCell sx={textCellSx(ui)}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography sx={{ fontSize: 13 }}>
                            {row.name || row.external_id || "—"}
                          </Typography>
                          {row.exceptions?.length ? (
                            <Tooltip title={row.exceptions.join(", ")}>
                              <WarningAmberIcon sx={{ fontSize: 14, color: ui.warning }} />
                            </Tooltip>
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell sx={textCellSx(ui)}>{row.order_name || "—"}</TableCell>
                      <TableCell sx={numCellSx(ui)}>
                        {row.age_days == null ? "—" : formatCount(row.age_days)}
                      </TableCell>
                      <TableCell sx={textCellSx(ui)}>
                        <Tooltip
                          title={
                            row.provider_status &&
                            row.status &&
                            row.provider_status.toLowerCase() !== String(row.status).toLowerCase()
                              ? `Display: ${row.status}`
                              : ""
                          }
                        >
                          <span>{row.stage_label || row.stage || "—"}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={numCellSx(ui)}>{formatCount(row.return_units)}</TableCell>
                      <TableCell sx={numCellSx(ui)}>{formatCurrency(row.return_value)}</TableCell>
                      <TableCell sx={textCellSx(ui)}>
                        {row.requested_at ? formatDate(row.requested_at, "—") : "—"}
                      </TableCell>
                      <TableCell sx={textCellSx(ui)}>{row.channel || "Unknown"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={payload?.total || 0}
                page={page}
                onPageChange={(_, next) => onPageChange(next)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
                rowsPerPageOptions={[10, 25, 50]}
                sx={{ fontSize: 12.5 }}
              />
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default OpenReturnAgeDrawer;
